/**
 * Scope of a permission. Permissions are scoped so that role/permission
 * assignment in the UI can be filtered (e.g. a CLIENT-scoped role can
 * only hold CLIENT- or GROUP-scoped permissions).
 *
 * Mirrors the Prisma `RoleScope` enum.
 */
export type PermissionScope = 'INTERNAL' | 'CLIENT' | 'GROUP' | 'BROKER' | 'TPA';

/**
 * Human-readable labels for each scope, used by the role/permission admin UI.
 * The key order is the canonical display order for scope selectors and filters.
 */
export const SCOPE_LABELS: Record<PermissionScope, string> = {
  INTERNAL: 'Internal (CBS staff)',
  CLIENT: 'Client (tenant admins)',
  GROUP: 'Group (employer staff)',
  BROKER: 'Broker (commission agents)',
  TPA: 'TPA (third-party administrators)',
};

/**
 * Ordered list of scopes, derived from `SCOPE_LABELS`. Use for rendering
 * scope `<Select>` options.
 */
export const SCOPE_OPTIONS = Object.keys(SCOPE_LABELS) as ReadonlyArray<PermissionScope>;

/**
 * Permission scopes assignable to any role. INTERNAL is intentionally excluded
 * so that only INTERNAL roles can be granted internal/cross-tenant permissions.
 */
const NON_INTERNAL_SCOPES = [
  'CLIENT',
  'GROUP',
  'BROKER',
  'TPA',
] as const satisfies readonly PermissionScope[];

/**
 * Explicit allowlist of which permission scopes each role scope may hold.
 *
 * - INTERNAL roles may hold permissions of any scope (including internal-only
 *   capabilities such as cross-tenant bypass).
 * - Every non-INTERNAL role may hold any non-INTERNAL permission, but never an
 *   INTERNAL-scoped one — this is what prevents a client/group/broker/TPA role
 *   from being granted cross-tenant access.
 *
 * Keyed by `PermissionScope`, so adding a new scope is a compile error here
 * until its compatibility is declared. Unlisted combinations are denied, so
 * the rule fails closed by default.
 */
const SCOPE_COMPATIBILITY: Record<PermissionScope, readonly PermissionScope[]> = {
  INTERNAL: ['INTERNAL', ...NON_INTERNAL_SCOPES],
  CLIENT: NON_INTERNAL_SCOPES,
  GROUP: NON_INTERNAL_SCOPES,
  BROKER: NON_INTERNAL_SCOPES,
  TPA: NON_INTERNAL_SCOPES,
};

/**
 * Whether a permission of `permissionScope` may be assigned to a role of
 * `roleScope`. Single source of truth shared by the role-management UI picker
 * and the server-side assignment guard in `rolesRouter`.
 *
 * Defensive at runtime: an unexpected/unknown scope (e.g. drifted DB data)
 * resolves to incompatible rather than silently allowing the assignment.
 */
export function isScopeCompatible(
  roleScope: PermissionScope,
  permissionScope: PermissionScope,
): boolean {
  return SCOPE_COMPATIBILITY[roleScope]?.includes(permissionScope) ?? false;
}

export type PermissionDefinition = {
  readonly key: string;
  readonly scope: PermissionScope;
  readonly description: string;
};

/**
 * Code-defined permission catalog.
 *
 * Every permission referenced via `requirePermission(key)` MUST be declared here.
 * The boot-time syncer (`apps/api/src/lib/syncPermissions.ts`) upserts these into
 * the `Permission` table with `source = CODE`. Permissions removed from this
 * catalog are flagged `isDeprecated = true` in the database (never deleted).
 *
 * Adding a permission:
 *   1. Add an entry below under the appropriate group.
 *   2. Reference it via `requirePermission('the.key')` (or in a code-side
 *      `ROLE_PERMISSIONS` entry) so the role inheritance map stays accurate.
 *   3. Assign it to roles either through the admin UI or, for `isSystem` roles,
 *      via the seeded role definitions (introduced in the Roles plan).
 *
 * Removing a permission:
 *   1. Remove the entry below.
 *   2. Remove every `requirePermission` reference.
 *   3. The next API boot will mark the DB row deprecated. Admins can then
 *      explicitly remove it from any roles via the UI.
 *
 * Naming convention: `<domain>.<action>` where `<domain>` matches the V1
 * spec section (page_access, accounts, brokers, members, phi, workflow,
 * user_management, governance) and `<action>` is bare snake_case from the
 * spec (e.g. `view_dashboard`, `create`, `edit_basic`). Existing internal
 * domains (payroll, rfc, onboarding, tenant, permissions,
 * roles) follow the same shape.
 */
export const CODE_PERMISSIONS = {
  // ---------------------------------------------------------------------
  // Existing INTERNAL-scope permissions (CBS staff).
  // ---------------------------------------------------------------------
  payroll: {
    debug: {
      key: 'payroll.debug',
      scope: 'INTERNAL',
      description:
        'View the debug panel on payroll upload results (batch details plus CBS file key, file check status, and Skynet record counts).',
    },
    overrideTestUpload: {
      key: 'payroll.override_test_upload',
      scope: 'INTERNAL',
      description:
        'Submit payroll uploads flagged as tests (group name is prefixed with TEST_ in CBS). Also controls the "This is a test upload" checkbox on the payroll upload page.',
    },
    clientAccess: {
      key: 'payroll.client_access',
      scope: 'CLIENT',
      description: 'Access payroll processing for groups within the client tenant.',
    },
  },

  rfc: {
    editProcessing: {
      key: 'rfc.edit_processing',
      scope: 'INTERNAL',
      description: 'Edit RFCs while they are in CBS_PROCESSING status.',
    },
    cbsQueue: {
      key: 'rfc.cbs_queue',
      scope: 'INTERNAL',
      description: 'See the ready-for-CBS queue widget and preset on RFC submission lists.',
    },
    viewPepm: {
      key: 'rfc.view_pepm',
      scope: 'CLIENT',
      description: 'View PEPM rate details on RFCs.',
    },
    view: {
      key: 'rfc.view',
      scope: 'CLIENT',
      description:
        'Open individual RFCs as a tenant user: read details, add notes, download attachments. ' +
        'Complements the page_access.* keys, which only gate the queue/pipeline list pages.',
    },
    edit: {
      key: 'rfc.edit',
      scope: 'CLIENT',
      description:
        'Edit RFCs as a tenant user: adjudicate, edit form data, archive/unarchive, regenerate access passwords, manage proposals. Also controls the Edit RFC button on deal review and the edit pencils on the account RFC tab.',
    },
  },

  prospects: {
    edit: {
      key: 'prospects.edit',
      scope: 'CLIENT',
      description:
        'Edit prospects as a tenant user: update details, move stages, manage notes/proposals, archive, convert to deal.',
    },
  },

  claimsTracking: {
    view: {
      key: 'claims_tracking.view',
      scope: 'INTERNAL',
      description: 'View the consolidated claims tracking dashboards.',
    },
  },

  audit: {
    view: {
      key: 'audit.view',
      scope: 'INTERNAL',
      description:
        'View the internal audit dashboards (invoice, member, VBA sync, Admin123, group) and the cross-tenant activity log. Roles without this do not see the Audit section in the internal portal sidebar.',
    },
  },

  enrollment: {
    view: {
      key: 'enrollment.view',
      scope: 'INTERNAL',
      description:
        'View the Enrollment section (public enroll-site monitor, site builder, submissions log) in the internal portal. Roles without this do not see Enrollment in the sidebar.',
    },
    manage: {
      key: 'enrollment.manage',
      scope: 'INTERNAL',
      description:
        'Create, edit, and delete enrollment sites in the builder (draft authoring). Roles without this can view sites but not change them.',
    },
  },

  groupPipeline: {
    view: {
      key: 'groupPipeline.view',
      scope: 'INTERNAL',
      description:
        'View the Groups section (group pipeline list, board, and group detail) in the internal portal. Roles without this do not see it in the sidebar.',
    },
    manage: {
      key: 'groupPipeline.manage',
      scope: 'INTERNAL',
      description:
        'Work a group pipeline: add groups, complete, skip, and edit tasks, change status (hold, not moving forward, move to step), and add notes.',
    },
  },

  reports: {
    viewIntake: {
      key: 'reports.view_intake',
      scope: 'INTERNAL',
      description: 'View the intake package completeness report (Reports & Analytics → Intake).',
    },
  },

  tickets: {
    manageInternal: {
      key: 'tickets.manage_internal',
      scope: 'INTERNAL',
      description:
        'Work the CBS-side portal ticket queue (list, review, status, engineering routing).',
    },
    triage: {
      key: 'tickets.triage',
      scope: 'CLIENT',
      description:
        'Triage portal tickets as a tenant admin (assign, comment, resolve). Client users without this cannot use ticketing and do not see the Tickets nav item.',
    },
    escalationReview: {
      key: 'tickets.escalation_review',
      scope: 'CLIENT',
      description: 'Review the dev-request escalation queue (send back to triage, forward to CBS).',
    },
    submissionAlerts: {
      key: 'tickets.submission_alerts',
      scope: 'CLIENT',
      description:
        'Eligible to opt into the new-ticket notification (in-app + email) when a portal ticket is submitted in the tenant. Delivery is off until the holder enables the TICKET_SUBMITTED notification setting. Intentionally in no default bundle — assign it to the designated ticket contact(s) only, not every admin.',
    },
    cbsSubmissionAlerts: {
      key: 'tickets.cbs_submission_alerts',
      scope: 'INTERNAL',
      description:
        'Eligible to opt into the email sent when a portal ticket reaches Clever Benefits and is awaiting internal review on the CBS ticket queue. Delivery is off until the holder enables the TICKET_CBS_REVIEW notification setting.',
    },
  },

  clients: {
    manageFeatures: {
      key: 'clients.manage_features',
      scope: 'INTERNAL',
      description: 'Edit per-tenant feature flags and portal configuration.',
    },
  },

  internalConfig: {
    manage: {
      key: 'internal_config.manage',
      scope: 'INTERNAL',
      description: 'Edit org-wide internal portal feature configuration.',
    },
  },

  eligibility: {
    view: {
      key: 'eligibility.view',
      scope: 'CLIENT',
      description:
        'View member eligibility data, including member search results. Roles without this do not see Member Search in the internal portal sidebar.',
    },
    update: {
      key: 'eligibility.update',
      scope: 'CLIENT',
      description: 'Update member eligibility data.',
    },
  },

  vip: {
    manage: {
      key: 'vip.manage',
      scope: 'CLIENT',
      description:
        'Manage VIP members for the tenant. Client users without this do not see VIP members in member search results.',
    },
  },

  externalTpa: {
    viewData: {
      key: 'external_tpa.view_data',
      scope: 'CLIENT',
      description:
        "See members and groups administered by the tenant's external third-party administrators — organizations other than CBS that run eligibility for part of the tenant's book of business. Client users without this do not see them in member search, group search, or the CSV export. This permission only widens access within a tenant that has external TPAs; it never grants CBS internal staff, brokers, or group-scoped users access to that data.",
    },
  },

  groups: {
    adminAccess: {
      key: 'groups.admin_access',
      scope: 'CLIENT',
      description:
        'Admin-level access to groups in the tenant (sensitive member fields such as SSN/DOB/external IDs).',
    },
    edit: {
      key: 'groups.edit',
      scope: 'CLIENT',
      description:
        'Edit group fields (name, address, contact, FEIN, billing cycle) and manage group plans and commission agents on a live group.',
    },
  },

  notifications: {
    manageOwn: {
      key: 'notifications.manage_own',
      scope: 'CLIENT',
      description:
        'Edit your own notification preferences on the Account Settings page. Users without this do not see the Notification Settings card.',
    },
    clientAdminLevel: {
      key: 'notifications.client_admin_level',
      scope: 'CLIENT',
      description: 'Receive and manage client-admin-level notifications.',
    },
    groupAdminLevel: {
      key: 'notifications.group_admin_level',
      scope: 'GROUP',
      description: 'Receive and manage group-admin-level notifications.',
    },
    cbsRfcHandoff: {
      key: 'notifications.cbs_rfc_handoff',
      scope: 'INTERNAL',
      description:
        'Receive the RFC ready-for-CBS handoff email when a deal is approved for implementation.',
    },
  },

  onboarding: {
    dryRun: {
      key: 'onboarding.dry_run',
      scope: 'INTERNAL',
      description:
        'Add or modify dry-run onboarding steps. Also controls the add/edit/delete step controls in the onboarding template editor.',
    },
  },

  tenant: {
    accessAll: {
      key: 'tenant.access_all',
      scope: 'INTERNAL',
      description:
        'Grants internal users cross-tenant access to client, group, and broker data (groups, members, agents, accounts, payroll, deals). Required for normal internal portal use and cannot be removed from a role.',
    },
  },

  permissions: {
    manage: {
      key: 'permissions.manage',
      scope: 'INTERNAL',
      description:
        'Manage the permission catalog (edit metadata, delete MANUAL permissions, retire deprecated entries).',
    },
  },

  roles: {
    manage: {
      key: 'roles.manage',
      scope: 'INTERNAL',
      description:
        'Manage the role catalog (CRUD CUSTOM roles, edit metadata, set permission edges, retire deprecated entries).',
    },
  },

  // ---------------------------------------------------------------------
  // V1 admin spec — CLIENT-scope permissions for client admin roles.
  // Each entry is named `<spec_domain>.<spec_action>`.
  // ---------------------------------------------------------------------
  pageAccess: {
    viewDashboard: {
      key: 'page_access.view_dashboard',
      scope: 'CLIENT',
      // Reserved: granted to client:admin but not yet enforced anywhere —
      // the dashboard currently renders for every client user. Wire a gate
      // before trimming this from a role has any effect.
      description: 'View main dashboard landing page. (Not yet enforced.)',
    },
    viewAccounts: {
      key: 'page_access.view_accounts',
      scope: 'CLIENT',
      description: 'View accounts list and detail pages.',
    },
    viewPipeline: {
      key: 'page_access.view_pipeline',
      scope: 'CLIENT',
      description: 'View pipeline kanban and list views.',
    },
    viewProspects: {
      key: 'page_access.view_prospects',
      scope: 'CLIENT',
      description: 'View prospects pipeline.',
    },
    viewMembers: {
      key: 'page_access.view_members',
      scope: 'CLIENT',
      description: 'View member list and detail pages.',
    },
    viewBrokers: {
      key: 'page_access.view_brokers',
      scope: 'CLIENT',
      description: 'View broker list, tree, and profiles.',
    },
    viewRfcQueue: {
      key: 'page_access.view_rfc_queue',
      scope: 'CLIENT',
      description:
        'View the RFC queue page (RFC submissions list, its CSV export, and proposal generator routes). Does not affect access to individual RFCs elsewhere.',
    },
    viewDocuments: {
      key: 'page_access.view_documents',
      scope: 'CLIENT',
      description: 'View document repository.',
    },
    viewResources: {
      key: 'page_access.view_resources',
      scope: 'CLIENT',
      description: 'View shared resource library.',
    },
    viewTraining: {
      key: 'page_access.view_training',
      scope: 'CLIENT',
      description: 'View training content.',
    },
    viewCommunications: {
      key: 'page_access.view_communications',
      scope: 'CLIENT',
      description: 'View communication threads.',
    },
    viewQuickProposal: {
      key: 'page_access.view_quick_proposal',
      scope: 'CLIENT',
      description: 'Use Quick Proposal tool.',
    },
    viewUtilizationReport: {
      key: 'page_access.view_utilization_report',
      scope: 'CLIENT',
      description:
        'Open the utilization report: the tenant-wide page and the Utilization tab on a group. The page token can see every group in the tenant; the group tab token is scoped to that one group.',
    },
    viewInformedAnalysis: {
      key: 'page_access.view_informed_analysis',
      scope: 'CLIENT',
      description: 'Use Informed Analysis tool.',
    },
    viewUserManagement: {
      key: 'page_access.view_user_management',
      scope: 'CLIENT',
      description: 'View user management settings.',
    },
    viewImports: {
      key: 'page_access.view_imports',
      scope: 'CLIENT',
      description: 'View Imports settings.',
    },
  },

  accounts: {
    create: {
      key: 'accounts.create',
      scope: 'CLIENT',
      description: 'Create a new account/group.',
    },
    editBasic: {
      key: 'accounts.edit_basic',
      scope: 'CLIENT',
      description: 'Edit basic account fields (notes, internal flags).',
    },
    movePipelineStageManual: {
      key: 'accounts.move_pipeline_stage_manual',
      scope: 'CLIENT',
      description: 'Manually move account between pipeline stages.',
    },
    archive: {
      key: 'accounts.archive',
      scope: 'CLIENT',
      description: 'Archive an account (soft-delete; retains audit history; no hard delete in V1).',
    },
    export: {
      key: 'accounts.export',
      scope: 'CLIENT',
      description: 'Export account list to CSV.',
    },
    uploadDocuments: {
      key: 'accounts.upload_documents',
      scope: 'CLIENT',
      description:
        'Upload documents to an account (Documents tab). Internal users must hold it too.',
    },
    admin: {
      key: 'accounts.admin',
      scope: 'CLIENT',
      description:
        'Admin actions on accounts: link VBA group, manage SWOT reviews, transfer ownership, skip sub-stages, edit assigned broker.',
    },
  },

  brokers: {
    add: {
      key: 'brokers.add',
      scope: 'CLIENT',
      description: 'Add a new broker (with W-9 prompt).',
    },
    editProfile: {
      key: 'brokers.edit_profile',
      scope: 'CLIENT',
      description:
        'Edit broker contact, commission, and license info (Tax ID, NPN, licensed states).',
    },
    editTree: {
      key: 'brokers.edit_tree',
      scope: 'CLIENT',
      description:
        'Move brokers within the upline/downline hierarchy. Controls drag-and-drop and the "Move Under..." action on the Brokers page.',
    },
    archive: {
      key: 'brokers.archive',
      scope: 'CLIENT',
      description:
        'Archive (deactivate) and reactivate brokers. Also controls the Archive/Reactivate buttons on the broker detail page.',
    },
    viewAs: {
      key: 'brokers.view_as',
      scope: 'CLIENT',
      description: 'Preview the broker portal as one of the tenant\u2019s brokers (read-only).',
    },
  },

  members: {
    add: {
      key: 'members.add',
      scope: 'CLIENT',
      description:
        'Add members to a group, individually or via eligibility file import. Also controls the Import File button on member search.',
    },
    edit: {
      key: 'members.edit',
      scope: 'CLIENT',
      description: 'Edit member record.',
    },
    terminate: {
      key: 'members.terminate',
      scope: 'CLIENT',
      description: "Terminate a member's coverage.",
    },
    exportCsv: {
      key: 'members.export_csv',
      scope: 'CLIENT',
      description: 'Export member list to CSV.',
    },
    editCoverage: {
      key: 'members.edit_coverage',
      scope: 'CLIENT',
      description: 'Edit member coverage: add or terminate products and change tiers.',
    },
    editDependents: {
      key: 'members.edit_dependents',
      scope: 'CLIENT',
      description: 'Add or drop dependents on a member record.',
    },
  },

  carrierReconciliation: {
    manage: {
      key: 'carrier_reconciliation.manage',
      scope: 'INTERNAL',
      description:
        'Upload carrier invoices, run reconciliation, and resolve billing discrepancies.',
    },
  },

  phi: {
    viewMemberSsnFull: {
      key: 'phi.view_member_ssn_full',
      scope: 'CLIENT',
      description: 'View full SSN (unmasked).',
    },
    viewMemberDob: {
      key: 'phi.view_member_dob',
      scope: 'CLIENT',
      description: 'View member date of birth.',
    },
  },

  workflow: {
    approveRfc: {
      key: 'workflow.approve_rfc',
      scope: 'CLIENT',
      description: 'Approve an RFC for admin review.',
    },
    sendMsaViaDocusign: {
      key: 'workflow.send_msa_via_docusign',
      scope: 'CLIENT',
      description: 'Send MSA via DocuSign integration.',
    },
    approveSendToCbs: {
      key: 'workflow.approve_send_to_cbs',
      scope: 'CLIENT',
      description: 'Approve and submit deal to CBS.',
    },
    editRfcInternalNotes: {
      key: 'workflow.edit_rfc_internal_notes',
      scope: 'CLIENT',
      description: 'Edit internal notes on an RFC.',
    },
  },

  userManagement: {
    inviteUser: {
      key: 'user_management.invite_user',
      scope: 'CLIENT',
      description: 'Invite a new user (Admin/Broker/GroupAdmin).',
    },
    editUserBasic: {
      key: 'user_management.edit_user_basic',
      scope: 'CLIENT',
      description: 'Edit basic user fields (name, email, phone).',
    },
    editUserRole: {
      key: 'user_management.edit_user_role',
      scope: 'CLIENT',
      description: "Change user's role within the same scope.",
    },
    editGroupAccess: {
      key: 'user_management.edit_group_access',
      scope: 'CLIENT',
      description: "Rewrite a group user's set of accessible groups.",
    },
    archiveUser: {
      key: 'user_management.archive_user',
      scope: 'CLIENT',
      description: 'Archive a user (soft-delete; no hard delete in V1).',
    },
    generateRegistrationCodes: {
      key: 'user_management.generate_registration_codes',
      scope: 'CLIENT',
      description: 'Generate user registration codes.',
    },
    revokeRegistrationCodes: {
      key: 'user_management.revoke_registration_codes',
      scope: 'CLIENT',
      description: 'Revoke unused registration codes.',
    },
    manageExternalUsers: {
      key: 'user_management.manage_external_users',
      scope: 'INTERNAL',
      description: 'Create, list, and deactivate external (server-to-server) API users.',
    },
  },

  governance: {
    sendBroadcastAnnouncement: {
      key: 'governance.send_broadcast_announcement',
      scope: 'CLIENT',
      description:
        'Send bulk broker messages (all brokers, filtered, or multiple recipients). Without this, users can only send to one broker at a time.',
    },
  },
} as const satisfies Record<string, Record<string, PermissionDefinition>>;

type Catalog = typeof CODE_PERMISSIONS;

/**
 * Compile-time union of every permission key in the catalog.
 *
 * `requirePermission` accepts this type for static safety, plus a `string`
 * escape hatch for the rare case where a permission key is built dynamically.
 */
export type CodePermissionKey = {
  [Group in keyof Catalog]: {
    [Item in keyof Catalog[Group]]: Catalog[Group][Item] extends {key: infer K} ? K : never;
  }[keyof Catalog[Group]];
}[keyof Catalog];

/**
 * Flat list of every permission definition in the catalog.
 * Used by the boot-time syncer and admin UI listings.
 */
export const ALL_CODE_PERMISSIONS: readonly PermissionDefinition[] = Object.values(
  CODE_PERMISSIONS,
).flatMap((group) => Object.values(group));

/**
 * Permission keys that can never be removed from a role through the admin UI.
 *
 * Removing `tenant.access_all` from an internal role silently breaks every
 * `*OrInternalAdmin` endpoint for that role's users — most of the internal
 * portal (groups, members, agents, accounts, payroll, deals). Because edited
 * roles are never re-synced from the code catalog, such a lockout would be
 * durable until an admin manually re-adds the key.
 *
 * Only removal is blocked: these keys can still be added to a role, and a
 * role created without them can stay without them (that's how restricted
 * internal roles are built). Enforced server-side in
 * `RolesService.setPermissions` and mirrored in the role editor UI.
 */
export const PROTECTED_PERMISSION_KEYS: ReadonlySet<string> = new Set([
  CODE_PERMISSIONS.tenant.accessAll.key,
]);

/**
 * Permission keys hidden from the permissions admin UI (catalog list and the
 * role editor's permission picker), so admins can't check or uncheck them.
 *
 * These keys stay in the code catalog: the boot syncer keeps their DB rows
 * alive, backend enforcement keeps compiling against them, and roles that
 * already hold them keep their edges (the role editor preserves unrendered
 * selections on save).
 *
 * No keys are currently hidden. `members.terminate` used to live here because
 * no portal UI called the disenroll/re-enroll endpoints; the member
 * terminate/reinstate flow now ships, so the key is assignable in the admin UI.
 */
export const HIDDEN_PERMISSION_KEYS: ReadonlySet<string> = new Set();
